import { goalbookUrls } from '@/util/links'
import { getOrStartRefresh, refreshJwtToken } from '@/authentication/request_handler'
import type { AlgoliaAPIKey, AlgoliaIndices } from '@/search/search_types'

const HTTP_UNAUTHORIZED = 401
const HTTP_SERVER_ERROR = 500
const KEY_REQUEST_TIMEOUT_MS = 10 * 1000

export class AlgoliaKeyError extends Error {
  readonly status: number | undefined

  constructor(message: string, { status, cause }: { status?: number, cause?: unknown } = {}) {
    super(message, { cause })
    this.name = 'AlgoliaKeyError'
    this.status = status
  }

  get isTransient(): boolean {
    return this.status === undefined || this.status >= HTTP_SERVER_ERROR
  }
}

// The key is fetched on demand, often with no user action behind it, so a lapsed session
// refreshes its token once and otherwise fails quietly rather than opening the login modal.
export async function getAlgoliaAPIKey(featureName: string, indices: AlgoliaIndices): Promise<AlgoliaAPIKey> {
  let response = await sendKeyRequest(featureName, indices)
  if (response.status === HTTP_UNAUTHORIZED && (await getOrStartRefresh(refreshJwtToken)).ok) {
    response = await sendKeyRequest(featureName, indices)
  }
  if (!response.ok) {
    throw new AlgoliaKeyError(`${response.status} status from the Algolia key endpoint`, { status: response.status })
  }
  return await response.json() as AlgoliaAPIKey
}

async function sendKeyRequest(featureName: string, indices: AlgoliaIndices): Promise<Response> {
  const params = new URLSearchParams({ indices: [indices].flat().join(','), feature_name: featureName })
  try {
    return await fetch(`${goalbookUrls.pathwaysApi}/algolia_api_key?${params.toString()}`, {
      method: 'GET',
      credentials: 'include',
      signal: AbortSignal.timeout(KEY_REQUEST_TIMEOUT_MS)
    })
  } catch (e) {
    throw new AlgoliaKeyError(`Network error reaching the Algolia key endpoint: ${String(e)}`, { cause: e })
  }
}
